import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    IconButton,
    AppBar,
    Toolbar,
    Container,
    CircularProgress,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    MenuItem,
    FormControl,
    Select
} from '@mui/material';
import {
    ArrowBack,
    EmojiEvents,
    Timer,
    SportsEsports,
    Groups,
    TrendingUp,
    WorkspacePremium
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface LeaderboardEntry {
    id: string;
    userId: string;
    userName: string;
    userPhotoURL: string;
    totalScore: number;
    gamesPlayed: number;
    gamesWon: number;
    averageScore: number;
    averageCompletionTime: number;
    rank: number;
    isPremium: boolean;
}

interface GameStats {
    totalPlayers: number;
    totalGamesPlayed: number;
    averageScore: number;
}

const GameStatisticsPage: React.FC = () => {
    const navigate = useNavigate();

    const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
    const [stats, setStats] = useState<GameStats>({ totalPlayers: 0, totalGamesPlayed: 0, averageScore: 0 });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalEntries, setTotalEntries] = useState(0);
    const [selectedMonth, setSelectedMonth] = useState<string>(new Date().toISOString().slice(0, 7));

    const PRIMARY_COLOR = '#2C3E50';
    const SECONDARY_COLOR = '#34495E';
    const ACCENT_COLOR = '#F1C40F'; // Gold for trophy

    const loadLeaderboard = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');

            const response = await axios.get(`${API_BASE_URL}/api/game/leaderboard`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: {
                    monthYear: selectedMonth,
                    limit: rowsPerPage,
                    offset: page * rowsPerPage
                }
            });

            if (response.data.success) {
                setEntries(response.data.data.leaderboard || []);
                setTotalEntries(response.data.data.meta.total || 0);
                setStats(response.data.data.meta.stats || { totalPlayers: 0, totalGamesPlayed: 0, averageScore: 0 });
            }

        } catch (error) {
            console.error('Error loading leaderboard:', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, selectedMonth]);

    useEffect(() => {
        loadLeaderboard();
    }, [loadLeaderboard]);

    const generateMonthOptions = () => {
        const options = [];
        const date = new Date();
        for (let i = 0; i < 6; i++) {
            const monthStr = date.toISOString().slice(0, 7);
            options.push(monthStr);
            date.setMonth(date.getMonth() - 1);
        }
        return options;
    };

    const formatTime = (seconds: number) => {
        return `${Math.floor(seconds)}s`;
    };

    if (loading && page === 0 && entries.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={80} sx={{ color: 'white', mb: 2 }} />
                    <Typography variant="h6" color="white">
                        İstatistikler Yükleniyor...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* App Bar */}
            <AppBar
                position="static"
                sx={{
                    background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                }}
            >
                <Toolbar sx={{ minHeight: 70 }}>
                    <IconButton
                        color="inherit"
                        onClick={() => navigate('/admin/dashboard')}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBack />
                    </IconButton>

                    <Typography variant="h5" component="div" sx={{
                        fontWeight: 700,
                        color: 'white',
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <EmojiEvents sx={{ color: ACCENT_COLOR }} />
                        Zirve Yarışı İstatistikleri
                    </Typography>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: 200, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}>
                        <Select
                            value={selectedMonth}
                            onChange={(e) => {
                                setSelectedMonth(e.target.value);
                                setPage(0);
                            }}
                            sx={{ color: 'white', '.MuiSvgIcon-root': { color: 'white' } }}
                        >
                            {generateMonthOptions().map(month => (
                                <MenuItem key={month} value={month}>{month}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* KPI Cards */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
                    <Box sx={{ flex: '1 1 300px' }}>
                        <Card sx={{
                            background: `linear-gradient(135deg, #1abc9c 0%, #16a085 100%)`,
                            color: 'white',
                            borderRadius: 3
                        }}>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <Groups sx={{ fontSize: 40, mb: 1, opacity: 0.8 }} />
                                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                    {stats.totalPlayers}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    Aktif Oyuncu (Bu Ay)
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 300px' }}>
                        <Card sx={{
                            background: `linear-gradient(135deg, #3498db 0%, #2980b9 100%)`,
                            color: 'white',
                            borderRadius: 3
                        }}>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <SportsEsports sx={{ fontSize: 40, mb: 1, opacity: 0.8 }} />
                                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                    {stats.totalGamesPlayed}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    Toplam Oyun
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 300px' }}>
                        <Card sx={{
                            background: `linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)`,
                            color: 'white',
                            borderRadius: 3
                        }}>
                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                <TrendingUp sx={{ fontSize: 40, mb: 1, opacity: 0.8 }} />
                                <Typography variant="h3" sx={{ fontWeight: 800 }}>
                                    {stats.averageScore}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                                    Ortalama Skor
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                {/* Leaderboard Table */}
                <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY_COLOR }}>
                            Liderlik Tablosu
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Sıra</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Toplam Puan</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Oyun Sayısı</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Kazanma Oranı</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Ort. Süre</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Üyelik</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((entry, index) => {
                                    const winRate = entry.gamesPlayed > 0
                                        ? Math.round((entry.gamesWon / entry.gamesPlayed) * 100)
                                        : 0;

                                    return (
                                        <TableRow key={entry.id} hover>
                                            <TableCell>
                                                <Box sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: '50%',
                                                    bgcolor: index < 3 ? ACCENT_COLOR : '#eee',
                                                    color: index < 3 ? 'white' : 'text.secondary',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 700
                                                }}>
                                                    {entry.rank || (page * rowsPerPage + index + 1)}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar
                                                        src={entry.userPhotoURL}
                                                        alt={entry.userName}
                                                        sx={{ width: 40, height: 40, border: entry.isPremium ? `2px solid ${ACCENT_COLOR}` : 'none' }}
                                                    />
                                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                        {entry.userName}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="h6" sx={{ color: PRIMARY_COLOR, fontWeight: 700 }}>
                                                    {entry.totalScore}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">{entry.gamesPlayed}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={`%${winRate}`}
                                                    size="small"
                                                    color={winRate > 50 ? 'success' : 'default'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: 'text.secondary' }}>
                                                    <Timer sx={{ fontSize: 16 }} />
                                                    {formatTime(entry.averageCompletionTime)}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                {entry.isPremium && (
                                                    <Chip
                                                        icon={<WorkspacePremium />}
                                                        label="Premium"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(241, 196, 15, 0.1)',
                                                            color: '#F39C12',
                                                            fontWeight: 600,
                                                            borderColor: '#F39C12'
                                                        }}
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {entries.length === 0 && !loading && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                Bu ay için veri bulunamadı.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={totalEntries}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e: any, newPage: any) => setPage(newPage)}
                        onRowsPerPageChange={(e: any) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Container>
        </Box>
    );
};

export default GameStatisticsPage;
